import { format, isSameDay } from 'date-fns';

export interface ValidationProblem {
  propertyName: string;
  message: string;
  errorCode?: string;
  metadata?: Record<string, unknown>;
}

export function parseValidationErrors(data: unknown): ValidationProblem[] {
  if (!Array.isArray(data)) return [];
  return data.filter(
    (x): x is ValidationProblem =>
      !!x && typeof x === 'object' && 'message' in x && typeof (x as { message: unknown }).message === 'string',
  );
}

export function validationMessage(data: unknown): string | null {
  const problems = parseValidationErrors(data);
  return problems.length > 0 ? problems.map((p) => p.message).join(' ') : null;
}

/**
 * Finds a double-booking style conflict window (`conflictStart`/`conflictEnd`
 * metadata) on a validation error response, and formats it in the local
 * timezone for display, e.g. "Jun 10, 2:00 PM – 6:00 PM".
 * Returns null when no such metadata is present.
 */
export function conflictWindowMessage(data: unknown): string | null {
  const conflict = parseValidationErrors(data).find(
    (p) =>
      typeof p.metadata?.conflictStart === 'string' &&
      typeof p.metadata?.conflictEnd === 'string',
  );
  if (!conflict?.metadata) return null;

  const start = new Date(conflict.metadata.conflictStart as string);
  const end = new Date(conflict.metadata.conflictEnd as string);
  if (isNaN(start.getTime()) || isNaN(end.getTime())) return null;

  const startLabel = format(start, 'MMM d, h:mm a');
  const endLabel = isSameDay(start, end)
    ? format(end, 'h:mm a')
    : format(end, 'MMM d, h:mm a');
  return `${startLabel} – ${endLabel}`;
}
